import React from "react";
import {
  Button,
  Flex,
  Heading,
  TextField,
  Divider,
  Text,
  SwitchField,
  TextAreaField,
  Card,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import { useUserQuery } from "../common/useUserQuery";
import {
  useHRImagingAndLabsQuery,
  useHRImagingAndLabsMediaQuery,
  MEDIA_QUERY_KEY,
} from "./api/useImagingAndLabsQuery";
import { useQueryClient } from "react-query";
import {
  useImagingPutDocumentMutation,
  useImagingPostDocumentMediaMutation,
} from "./api/useImagingAndLabsMutation";
import { StorageManager } from "@aws-amplify/ui-react-storage";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "rsuite/DatePicker";
import { formatDate } from "../common/useDateFunctions";
import { FiTrash } from "react-icons/fi";
import ImageComponent from "../common/ImageComponent";
import { zodResolver } from "@hookform/resolvers/zod";
const { z } = require("zod");

const documentSchema = z.object({
  documentName: z
    .string()
    .min(1, { message: "Document Name is required" })
    .max(100, { message: "Must be 100 characters or fewer" }),
  documentType: z
    .string()
    .min(1, { message: "Document Type is required" })
    .max(100, { message: "Must be 100 characters or fewer" }),
  documentDate: z.union([
    z.number({ message: "Invalid Format. YYYY-MM-DD Format" }),
    z.string({ message: "Invalid Format. YYYY-MM-DD Format" }),
    z.date({ message: "Invalid Format. YYYY-MM-DD Format" }),
  ]),
  description: z
    .string()
    .min(1, { message: "Description is required" })
    .max(250, { message: "Must be 250 characters or fewer" }),
  active: z.number().int(),
  medias: z.any(),
});

const acceptedFileTypes = ["image/*", "video/*"];
const defaultValues = {
  documentName: "",
  documentType: "",
  documentDate: "",
  description: "",
  active: false,
  medias: [],
};

export default function ImagingAndLabsEditPage() {
  const navigate = useNavigate();
  const params = useParams();
  const queryClient = useQueryClient();

  const { data: userQueryData } = useUserQuery();
  const putDocumentMutation = useImagingPutDocumentMutation();
  const postDocumentMediaMutation = useImagingPostDocumentMediaMutation();
  const {
    isPending,
    error,
    data: documentResults,
    isFetching,
    isSuccess,
  } = useHRImagingAndLabsQuery(params.id);

  const { data: mediaResults } = useHRImagingAndLabsMediaQuery(params.id);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues,
    values: { ...documentResults, medias: mediaResults },
    resolver: zodResolver(documentSchema),
  });

  if (isPending) return "Loading...";

  if (error) return "An error has occurred: " + error.message;

  if (isSuccess)
    return (
      <>
        <div>{isFetching ? "Updating..." : ""}</div>
        <form
          onSubmit={handleSubmit(async (values) => {
            try {
              const tmpDate = new Date(values.documentDate);
              const documentParams = {
                id: documentResults.id,
                documentName: values.documentName,
                documentType: values.documentType,
                documentDate: formatDate(tmpDate),
                description: values.description,
                active: values.active,
              };

              putDocumentMutation.mutate(documentParams);

              if (
                values.medias &&
                values.medias?.length > 0 &&
                documentResults?.id !== ""
              ) {
                const tmpMedias = values.medias.filter(
                  (media) => typeof media === "string"
                );

                if (tmpMedias.length > 0)
                  postDocumentMediaMutation.mutate({
                    documentGroupId: documentResults?.id,
                    patientUserId: userQueryData?.patientUserId,
                    medias: tmpMedias,
                  });
              }

              reset();
              navigate("/user/hr/documents/");
            } catch (error) {
              console.log("Error : ", error);
            }
          })}
        >
          <FlexFormContainer headerTitle={"Imaging and Labs Results"}>
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              style={{ width: "100%" }}
              gap={32}
            >
              <Flex
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Controller
                  name="documentName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      minWidth="300px"
                      label="Document name"
                      {...field}
                      value={field.value ?? documentResults?.documentName}
                      errorMessage={errors.documentName?.message}
                      hasError={!!errors.documentName}
                    />
                  )}
                />
                <Controller
                  name="documentType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      minWidth="300px"
                      label="Document type"
                      {...field}
                      value={field.value ?? documentResults?.documenType}
                      errorMessage={errors.documentType?.message}
                      hasError={!!errors.documentType}
                    />
                  )}
                />
                <Controller
                  name="documentDate"
                  control={control}
                  render={({ field }) => (
                    <>
                      <DatePicker
                        id={field.name}
                        onChange={(value) => field.onChange(value)}
                        value={
                          field.value != null ? new Date(field.value) : null
                        }
                        format="yyyy-MM-dd"
                        oneTap
                        style={{ width: "300px" }}
                        size="lg"
                      />
                      {!!errors.documentDate && (
                        <Text
                          variation="error"
                          as="p"
                          lineHeight="0.5em"
                          fontWeight={400}
                          fontSize="0.8em"
                          fontStyle="normal"
                          textDecoration="none"
                          width="30vw"
                          style={{ marginLeft: "10px" }}
                        >
                          {errors.documentDate.message}
                        </Text>
                      )}
                    </>
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextAreaField
                      minWidth="300px"
                      label="Description"
                      {...field}
                      value={field.value ?? documentResults?.description}
                      errorMessage={errors.description?.message}
                      hasError={!!errors.description}
                    />
                  )}
                />
                <Divider
                  width="unset"
                  height="1px"
                  shrink="0"
                  alignSelf="stretch"
                  size="small"
                  orientation="horizontal"
                ></Divider>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <SwitchField
                      label="is Active?"
                      labelPosition="end"
                      {...field}
                      isChecked={field.value ?? documentResults?.active === 1}
                      onChange={(event) =>
                        field.onChange(event.target.checked ? 1 : 0)
                      }
                    />
                  )}
                />
              </Flex>
            </Flex>
            <Flex
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              gap={0}
            >
              <Heading level="6" children="Attachments"></Heading>
              {mediaResults.length > 0 &&
                mediaResults?.map((media) => {
                  return (
                    <>
                      <Card key={media.id} style={{ padding: "0px" }}>
                        <Flex direction="row" alignItems="flex-start" gap={1}>
                          <ImageComponent />
                          <Flex
                            direction="column"
                            alignItems="flex-start"
                            gap={1}
                          >
                            <Button size="small" variation="link">
                              {media.name}
                            </Button>

                            <Text
                              as="span"
                              fontSize="12px"
                              style={{ marginLeft: "12px" }}
                            >
                              Type:{" "}
                              {media.url.split(".").pop() === "mp4"
                                ? "Video"
                                : "Image"}
                            </Text>

                            {/* <Flex
                              direction="row"
                              gap={1}
                              style={{ marginLeft: "10px" }}
                            >
                              <Button
                                size="small"
                                variation="link"
                                style={{
                                  paddingRight: "4px",
                                  paddingLeft: "4px",
                                }}
                                onClick={async () => {
                                  // deleteDocumentMediaMutation.mutateAsync({
                                  //   id: document.id,
                                  // });
                                }}
                              >
                                <FiTrash />
                              </Button>
                            </Flex> */}
                          </Flex>
                        </Flex>
                      </Card>
                      <Divider
                        width="unset"
                        height="1px"
                        shrink="0"
                        alignSelf="stretch"
                        size="small"
                        orientation="horizontal"
                        style={{ marginTop: "0px" }}
                      ></Divider>
                    </>
                  );
                })}

              {mediaResults.length === 0 && (
                <Text
                  fontSize="12px"
                  style={{
                    marginTop: "10px",
                    marginLeft: "10px",
                    marginBottom: "10px",
                  }}
                >
                  No attachments found.
                </Text>
              )}

              <Flex direction="row">
                <Controller
                  name="medias"
                  control={control}
                  render={({
                    field: { onChange, value: frontValue, name },
                  }) => (
                    <StorageManager
                      name={name}
                      autoUpload={false}
                      acceptedFileTypes={acceptedFileTypes}
                      path={`public/users/${userQueryData?.patientUserId}/documents/`}
                      maxFileCount={20}
                      isResumable
                      onUploadSuccess={({ key }) => {
                        const value = getValues("medias");

                        if (JSON.stringify(value) === "{}") {
                          setValue("medias", [key]);
                        } else {
                          setValue("medias", [...value, key]);
                        }
                      }}
                      onUploadError={(err, fi) => {
                        console.log("error", { err, fi });
                      }}
                    />
                  )}
                />
              </Flex>
              <Button
                variation="primary"
                size="small"
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: [MEDIA_QUERY_KEY],
                  });
                  navigate(
                    `/user/hr/documents/${params.id}/preview/${documentResults.documentName}`
                  );
                }}
              >
                Preview Files
              </Button>
            </Flex>
            <Flex gap="12" direction="column">
              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
}
